import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { AuthService } from '../services/api';
import { toast } from 'sonner';

interface GoogleSignInButtonProps {
  /** Disable while another auth action is in flight. */
  disabled?: boolean;
  /** New Google account — the caller shows the username step. */
  onNeedsUsername: (profile: { uid: string; email: string; name: string; photoURL: string }) => void;
  /** Returning Google account — already signed in, nothing else to collect. */
  onSignedIn: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

/**
 * Google sign-in entry point, shared by the Login and Create Account screens.
 * Styled after the existing "Download on App Store" outline button so it reads
 * as a secondary option next to the gradient primary buttons.
 */
export function GoogleSignInButton({ disabled, onNeedsUsername, onSignedIn, onLoadingChange }: GoogleSignInButtonProps) {
  const [loading, setLoading] = useState(false);

  const setBusy = (v: boolean) => { setLoading(v); onLoadingChange?.(v); };

  const handleClick = async () => {
    if (loading || disabled) return;
    setBusy(true);
    try {
      const result = await AuthService.googleLogin();
      if (result.isNewUser) {
        onNeedsUsername(result);
      } else {
        onSignedIn();
      }
    } catch (e: any) {
      const msg = String(e?.message || e);
      // The user closing the Google sheet isn't an error worth shouting about.
      if (/popup-closed|cancel|user_cancel|1000|closed by user/i.test(msg)) return;

      const code = String(e?.code || '');
      console.error('[GoogleSignIn] failed:', code || '(no code)', e);

      // "Try again" is the wrong advice for most of these — trying again does
      // nothing when the domain is unauthorised or the email is already taken by
      // a password account. Say which wall was hit.
      const reason =
        code === 'auth/account-exists-with-different-credential'
          ? 'That email already has an account here. Sign in with your username and password instead.'
        : code === 'auth/unauthorized-domain'
          ? 'Google sign-in is not allowed from this address yet.'
        : code === 'auth/popup-blocked'
          ? 'Your browser blocked the Google window. Allow pop-ups and try again.'
        : code === 'auth/operation-not-allowed'
          ? 'Google sign-in is not enabled for this app yet.'
        : code === 'auth/network-request-failed'
          ? 'Network problem reaching Google. Check your connection and try again.'
        : /GOOGLE_POPUP_TIMEOUT/.test(msg)
          ? 'Google took too long to respond. Try again.'
        : /GOOGLE_NO_ID_TOKEN/.test(msg)
          ? 'Google did not return a sign-in token.'
          : 'Google sign-in failed. Please try again.';

      toast.error(reason, code ? { description: code } : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleClick}
      disabled={disabled || loading}
      className="w-full max-w-[280px] mx-auto h-12 rounded-xl font-bold border-2 border-black/5 dark:border-white/10 bg-white/50 dark:bg-white/5 backdrop-blur-md hover:bg-white/80 dark:hover:bg-white/10 transition-all flex items-center justify-center gap-3 text-sm text-black dark:text-white disabled:opacity-50"
    >
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z" />
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
      )}
      <span>{loading ? 'Connecting…' : 'Continue with Google'}</span>
    </Button>
  );
}
